/**
 * AuthLayout Component
 * Shared layout for authentication pages (Login, Register)
 * Handles GlobalHeader with marquee and centered form layout
 *
 * Design Reference: docs/auth-pages-redesign.md
 */

import type { ReactNode } from 'react';
import GlobalHeader from '../common/GlobalHeader';
import { useMarquees } from '../../hooks/useMarquees';
import './AuthLayout.css';

interface AuthLayoutProps {
  children: ReactNode;
}

/**
 * AuthLayout - Layout wrapper for authentication pages
 *
 * Layout structure:
 * ┌─────────────────────────────────┐
 * │  MarqueeBanner (if enabled)      │ ← Fixed at top (managed by GlobalHeader)
 * ├─────────────────────────────────┤
 * │  GlobalHeader                    │ ← Fixed below marquee or at top
 * ├─────────────────────────────────┤
 * │  Form Content (centered)         │ ← Flex centered, full viewport height
 * └─────────────────────────────────┘
 *
 * Features:
 * - Background image with dark overlay (desktop)
 * - Centered form layout
 * - Responsive height calculation based on marquee visibility
 *
 * @example
 * <AuthLayout>
 *   <LoginForm />
 * </AuthLayout>
 */
const AuthLayout = ({ children }: AuthLayoutProps) => {
  const { enabled: marqueeEnabled, loading: marqueeLoading } = useMarquees();

  // Marquee is shown when both enabled and not loading
  const shouldShowMarquee = marqueeEnabled && !marqueeLoading;

  return (
    <div className="auth-layout">
      <GlobalHeader />
      <main className={`auth-layout__content ${shouldShowMarquee ? 'auth-layout__content--with-marquee' : ''}`}>
        <div className="auth-layout__form-wrapper">
          {children}
        </div>
      </main>
    </div>
  );
};

export default AuthLayout;
